import { useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, euro, datum } from '../../../gemeinsam/lib/daten';
import {
  Abzeichen, Auswahl, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner, TarifSperre,
} from '../../../gemeinsam/komponenten/Basis';

type Fahrzeug = { id: string; kennzeichen: string; bezeichnung: string; kmStart: number; aktuellerKmStand: number; fahrtenAnzahl: number };
type Fahrt = {
  id: string; nr: number; datum: string; art: 'geschaeftlich' | 'arbeitsweg' | 'privat';
  kmBeginn: number; kmEnde: number; kilometer: number;
  ziel: string; zweck: string; geschaeftspartner: string; umweg: string;
  nachgetragen: boolean; nachgetragenTage: number; festgeschrieben: boolean;
};
type Antwort = {
  fahrten: Fahrt[];
  summen: {
    geschaeftlich: { fahrten: number; km: number };
    arbeitsweg: { fahrten: number; km: number };
    privat: { fahrten: number; km: number };
    gesamtKm: number; geschaeftlicherAnteil: number; pauschaleGeschaeftlich: number;
  };
};

const ART_NAME = { geschaeftlich: 'Geschäftlich', arbeitsweg: 'Arbeitsweg', privat: 'Privat' } as const;

export default function Fahrtenbuch() {
  const { daten: fahrzeuge, laedt, tarifsperre, neuLaden: fahrzeugeNeu } = useDaten<Fahrzeug[]>('/fahrtenbuch/fahrzeuge');
  const [gewaehlt, setGewaehlt] = useState('');

  const fahrzeugId = gewaehlt || fahrzeuge?.[0]?.id || '';
  const { daten, neuLaden } = useDaten<Antwort>(
    fahrzeugId ? `/fahrtenbuch/fahrten?fahrzeugId=${fahrzeugId}` : '/fahrtenbuch/fahrten', [fahrzeugId]);

  const [neuesFahrzeug, setNeuesFahrzeug] = useState<{ kennzeichen: string; bezeichnung: string; kmStart: string } | null>(null);
  const [fahrt, setFahrt] = useState<Record<string, string> | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  if (tarifsperre) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Fahrtenbuch</h1>
        <TarifSperre was="Das Fahrtenbuch" upgradeAuf={tarifsperre.upgradeAuf} />
      </div>
    );
  }
  if (laedt && !fahrzeuge) return <div className="flex justify-center py-16"><Spinner /></div>;

  const aktuell = fahrzeuge?.find(f => f.id === fahrzeugId);

  async function fahrzeugAnlegen(e: React.FormEvent) {
    e.preventDefault();
    if (!neuesFahrzeug) return;
    setSendet(true);
    setFehler(null);
    try {
      await api('/fahrtenbuch/fahrzeuge', {
        methode: 'POST',
        daten: { ...neuesFahrzeug, kmStart: Number(neuesFahrzeug.kmStart) || 0 },
      });
      setNeuesFahrzeug(null);
      setMeldung('Fahrzeug angelegt.');
      await fahrzeugeNeu();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Anlegen fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  async function fahrtSpeichern(e: React.FormEvent) {
    e.preventDefault();
    if (!fahrt) return;
    setSendet(true);
    setFehler(null);
    try {
      await api('/fahrtenbuch/fahrten', {
        methode: 'POST',
        daten: {
          ...fahrt, fahrzeugId,
          kmBeginn: Number(fahrt.kmBeginn) || 0,
          kmEnde: Number(fahrt.kmEnde) || 0,
        },
      });
      setFahrt(null);
      setMeldung('Fahrt eingetragen.');
      await Promise.all([neuLaden(), fahrzeugeNeu()]);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Eintragen fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  async function festschreiben() {
    if (!confirm('Alle bisherigen Fahrten festschreiben?\n\nDanach sind sie unveränderlich — ein nachträglich änderbares Fahrtenbuch erkennt das Finanzamt nicht an.')) return;
    try {
      const r = await api<{ festgeschrieben: number }>('/fahrtenbuch/festschreiben', { methode: 'POST', daten: {} });
      setMeldung(`${r.festgeschrieben} Fahrten festgeschrieben.`);
      await neuLaden();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Festschreiben fehlgeschlagen.');
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Fahrtenbuch</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Nach § 6 Abs. 1 Nr. 4 EStG — lückenlos und unveränderlich
          </p>
        </div>
        <div className="flex gap-2">
          {aktuell && !fahrt && (
            <Knopf variante="primaer" onClick={() => {
              setFahrt({
                art: 'geschaeftlich', kmBeginn: String(aktuell.aktuellerKmStand), kmEnde: '',
                ziel: '', zweck: '', geschaeftspartner: '', umweg: '',
                datum: new Date().toISOString().slice(0, 10),
              });
              setMeldung(null);
            }}>Fahrt eintragen</Knopf>
          )}
          {!neuesFahrzeug && (
            <Knopf onClick={() => setNeuesFahrzeug({ kennzeichen: '', bezeichnung: '', kmStart: '0' })}>
              Fahrzeug
            </Knopf>
          )}
        </div>
      </div>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      {neuesFahrzeug && (
        <Karte>
          <KartenKopf titel="Neues Fahrzeug" />
          <form onSubmit={fahrzeugAnlegen} className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <Feld label="Kennzeichen" value={neuesFahrzeug.kennzeichen} required autoFocus
                    onChange={e => setNeuesFahrzeug(f => f && ({ ...f, kennzeichen: e.target.value }))} />
              <Feld label="Bezeichnung" value={neuesFahrzeug.bezeichnung}
                    onChange={e => setNeuesFahrzeug(f => f && ({ ...f, bezeichnung: e.target.value }))} />
              <Feld label="Kilometerstand jetzt" value={neuesFahrzeug.kmStart} inputMode="numeric"
                    hinweis="Startwert des Fahrtenbuchs."
                    onChange={e => setNeuesFahrzeug(f => f && ({ ...f, kmStart: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <Knopf type="submit" variante="primaer" laedt={sendet}>Anlegen</Knopf>
              <Knopf type="button" onClick={() => setNeuesFahrzeug(null)}>Abbrechen</Knopf>
            </div>
          </form>
        </Karte>
      )}

      {!fahrzeuge?.length ? (
        <Karte><LeerZustand titel="Noch kein Fahrzeug" text="Legen Sie zuerst ein Fahrzeug mit seinem aktuellen Kilometerstand an." /></Karte>
      ) : (
        <>
          {fahrzeuge.length > 1 && (
            <Karte klasse="p-4">
              <Auswahl label="Fahrzeug" value={fahrzeugId} onChange={e => setGewaehlt(e.target.value)}>
                {fahrzeuge.map(f => (
                  <option key={f.id} value={f.id}>{f.kennzeichen}{f.bezeichnung ? ` — ${f.bezeichnung}` : ''}</option>
                ))}
              </Auswahl>
            </Karte>
          )}

          {daten && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { t: 'Geschäftlich', w: `${daten.summen.geschaeftlich.km} km`, z: `${daten.summen.geschaeftlich.fahrten} Fahrten`, f: 'text-emerald-700 dark:text-emerald-300' },
                { t: 'Privat', w: `${daten.summen.privat.km} km`, z: `${daten.summen.privat.fahrten} Fahrten`, f: 'text-slate-900 dark:text-slate-100' },
                { t: 'Geschäftlicher Anteil', w: `${daten.summen.geschaeftlicherAnteil} %`, z: 'bestimmt die Kostenaufteilung', f: 'text-indigo-700 dark:text-indigo-300' },
                { t: 'Kilometerpauschale', w: euro(daten.summen.pauschaleGeschaeftlich), z: '0,30 € je km', f: 'text-violet-700 dark:text-violet-300' },
              ].map(k => (
                <Karte key={k.t} klasse="p-4">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{k.t}</p>
                  <p className={`mt-1 text-xl font-bold ${k.f}`}>{k.w}</p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{k.z}</p>
                </Karte>
              ))}
            </div>
          )}

          {fahrt && aktuell && (
            <Karte>
              <KartenKopf titel="Fahrt eintragen"
                          hinweis={`Kilometerstand muss bei ${aktuell.aktuellerKmStand} km anschließen.`} />
              <form onSubmit={fahrtSpeichern} className="space-y-4 p-5">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Auswahl label="Art der Fahrt" value={fahrt.art}
                           onChange={e => setFahrt(f => f && ({ ...f, art: e.target.value }))}>
                    <option value="geschaeftlich">Geschäftlich</option>
                    <option value="arbeitsweg">Arbeitsweg</option>
                    <option value="privat">Privat</option>
                  </Auswahl>
                  <Feld label="Datum" type="date" value={fahrt.datum}
                        onChange={e => setFahrt(f => f && ({ ...f, datum: e.target.value }))} />
                  <Feld label="Kilometer bei Abfahrt" value={fahrt.kmBeginn} inputMode="numeric" required
                        onChange={e => setFahrt(f => f && ({ ...f, kmBeginn: e.target.value }))} />
                  <Feld label="Kilometer bei Ankunft" value={fahrt.kmEnde} inputMode="numeric" required autoFocus
                        onChange={e => setFahrt(f => f && ({ ...f, kmEnde: e.target.value }))} />
                </div>

                {fahrt.art === 'geschaeftlich' && (
                  <>
                    <Hinweis art="info">
                      Bei geschäftlichen Fahrten verlangt das Finanzamt Reiseziel, Reisezweck und den
                      aufgesuchten Geschäftspartner. Fehlt eine Angabe, ist das Fahrtenbuch angreifbar.
                    </Hinweis>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <Feld label="Reiseziel" value={fahrt.ziel} required
                            onChange={e => setFahrt(f => f && ({ ...f, ziel: e.target.value }))} />
                      <Feld label="Reisezweck" value={fahrt.zweck} required
                            onChange={e => setFahrt(f => f && ({ ...f, zweck: e.target.value }))} />
                      <Feld label="Geschäftspartner" value={fahrt.geschaeftspartner} required
                            onChange={e => setFahrt(f => f && ({ ...f, geschaeftspartner: e.target.value }))} />
                    </div>
                    <Feld label="Begründung bei Umwegen (optional)" value={fahrt.umweg}
                          onChange={e => setFahrt(f => f && ({ ...f, umweg: e.target.value }))} />
                  </>
                )}

                <div className="flex gap-2">
                  <Knopf type="submit" variante="primaer" laedt={sendet}>Eintragen</Knopf>
                  <Knopf type="button" onClick={() => { setFahrt(null); setFehler(null); }}>Abbrechen</Knopf>
                </div>
              </form>
            </Karte>
          )}

          <Karte>
            <KartenKopf
              titel="Fahrten"
              hinweis={daten ? `${daten.fahrten.length} Einträge, neueste zuerst` : undefined}
              aktion={daten?.fahrten.some(f => !f.festgeschrieben)
                ? <Knopf onClick={festschreiben}>Festschreiben</Knopf> : undefined}
            />
            {!daten?.fahrten.length ? (
              <LeerZustand titel="Noch keine Fahrten" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-2.5 font-medium">Nr</th>
                      <th className="px-3 py-2.5 font-medium">Datum</th>
                      <th className="px-3 py-2.5 font-medium">Art</th>
                      <th className="px-3 py-2.5 font-medium">Ziel und Zweck</th>
                      <th className="px-3 py-2.5 text-right font-medium">km</th>
                      <th className="px-5 py-2.5 text-right font-medium">Stand</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {daten.fahrten.map(f => (
                      <tr key={f.id}>
                        <td className="px-5 py-2.5 text-slate-500 dark:text-slate-400">{f.nr}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-slate-600 dark:text-slate-400">
                          {datum(f.datum)}
                          {f.nachgetragen && (
                            <span className="ml-1.5" title={`${f.nachgetragenTage} Tage später eingetragen`}>
                              <Abzeichen art="gelb">nachgetragen</Abzeichen>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <Abzeichen art={f.art === 'geschaeftlich' ? 'gruen' : f.art === 'arbeitsweg' ? 'blau' : 'neutral'}>
                            {ART_NAME[f.art]}
                          </Abzeichen>
                        </td>
                        <td className="px-3 py-2.5">
                          {f.art === 'privat' ? (
                            <span className="text-slate-400 dark:text-slate-500">—</span>
                          ) : (
                            <>
                              <p className="text-slate-900 dark:text-slate-100">{f.ziel}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {f.zweck}{f.geschaeftspartner ? ` · ${f.geschaeftspartner}` : ''}
                              </p>
                            </>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-slate-900 dark:text-slate-100">{f.kilometer}</td>
                        <td className="px-5 py-2.5 text-right text-xs text-slate-500 dark:text-slate-400">
                          {f.kmBeginn} → {f.kmEnde}
                          {f.festgeschrieben && <span className="ml-1.5" title="festgeschrieben">🔒</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Karte>
        </>
      )}
    </div>
  );
}
